import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  COUNTRIES,
  COUNTRY_NAMES,
  PROVINCES,
  TEAM_PAY_MODE_LABELS,
  TEAM_PAY_MODES,
  territoryLabel,
  VISIBILITIES,
  VISIBILITY_LABELS,
  type SellerCreated,
  type SellerDTO,
  type SellerKind,
  type TeamPayMode,
  type TerritoryArea,
  type Visibility,
} from '@bifeai/shared';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  Dialog,
  EmptyState,
  Field,
  Input,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  PageHeader,
  Select,
  Skeleton,
  Switch,
  Textarea,
  useToast,
} from '@bifeai/ui';
import { Copy, Ellipsis, Handshake, KeyRound, Pencil, Plus, Trash2, UserCheck, UserX, Users, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { copy, fieldsOf } from '../components/sellers';
import { api, errorMessage } from '../lib/api';
import { formatDate, formatUsd } from '../lib/format';
import { useCan } from '../lib/session';

const TEXT: Record<SellerKind, { title: string; subtitle: string; add: string; empty: string; one: string }> = {
  SELLER: {
    title: 'Vendedores',
    subtitle: 'Venden a comisión. Cada uno entra al panel y ve solo lo suyo: sus prospectos, sus clientes y sus comisiones.',
    add: 'Sumar vendedor',
    empty: 'Sin vendedores todavía',
    one: 'vendedor',
  },
  PARTNER: {
    title: 'Partners',
    subtitle: 'Socios con un acuerdo propio: territorio, exclusividad, qué cobran y qué ven de sus clientes. Pueden tener su red de vendedores.',
    add: 'Sumar partner',
    empty: 'Sin partners todavía',
    one: 'partner',
  },
};

export const PartnersPage = () => <PeoplePage kind="PARTNER" />;
export const SellersPage = () => <PeoplePage kind="SELLER" />;

/** Vendedores o partners: ficha, acuerdo, red, % de comisión y acceso a su portal. */
function PeoplePage({ kind }: { kind: SellerKind }) {
  const qc = useQueryClient();
  const toast = useToast();
  const can = useCan();
  const manage = can('sellers.manage');
  const t = TEXT[kind];
  const [editing, setEditing] = useState<SellerDTO | 'new' | null>(null);
  const [secret, setSecret] = useState<{ title: string; email: string; password: string; refUrl?: string } | null>(null);
  const [removing, setRemoving] = useState<SellerDTO | null>(null);
  const list = useQuery({ queryKey: ['sellers'], queryFn: () => api.get<SellerDTO[]>('/platform/sellers') });
  const refresh = () => qc.invalidateQueries({ queryKey: ['sellers'] });

  const active = useMutation({
    mutationFn: (s: SellerDTO) => api.post(`/platform/sellers/${s.id}/active`, { active: !s.isActive }),
    onSuccess: async () => {
      toast.success('Listo');
      await refresh();
    },
    onError: (err) => toast.error('No se pudo cambiar', errorMessage(err)),
  });
  const reset = useMutation({
    mutationFn: (s: SellerDTO) =>
      api.post<{ temporaryPassword: string }>(`/platform/sellers/${s.id}/reset-password`).then((r) => ({ s, password: r.temporaryPassword })),
    onSuccess: ({ s, password }) => setSecret({ title: `Nueva contraseña temporal de ${s.name}`, email: s.email, password }),
    onError: (err) => toast.error('No se pudo resetear', errorMessage(err)),
  });
  const remove = useMutation({
    mutationFn: (s: SellerDTO) => api.del<{ result: 'DELETED' | 'DEACTIVATED' }>(`/platform/sellers/${s.id}`),
    onSuccess: async (r) => {
      toast.success(r.result === 'DELETED' ? 'Borrado' : 'Tenía ventas: quedó dado de baja con su historial');
      setRemoving(null);
      await refresh();
    },
    onError: (err) => toast.error('No se pudo borrar', errorMessage(err)),
  });

  const everyone = list.data ?? [];
  const rows = everyone.filter((s) => s.kind === kind);
  const partner = kind === 'PARTNER';
  return (
    <>
      <PageHeader
        title={t.title}
        subtitle={t.subtitle}
        actions={
          manage && (
            <Button variant="primary" icon={<Plus />} onClick={() => setEditing('new')}>
              {t.add}
            </Button>
          )
        }
      />
      <Card>
        <CardBody flush>
          {list.isPending ? (
            <Skeleton style={{ height: 180 }} />
          ) : !rows.length ? (
            <EmptyState icon={partner ? <Handshake /> : <Users />} title={t.empty} text={`Sumá tu primer ${t.one}: le llega su acceso y su link.`} />
          ) : (
            <div className="bf-table-wrap">
              <table className="bf-table">
                <thead>
                  <tr>
                    <th>{partner ? 'Partner' : 'Vendedor'}</th>
                    <th>{partner ? 'Territorio' : 'Zona'}</th>
                    <th>Comisión</th>
                    <th>Red</th>
                    <th style={{ textAlign: 'right' }}>Clientes</th>
                    <th style={{ textAlign: 'right' }}>A pagar</th>
                    <th style={{ textAlign: 'right' }}>Pagado</th>
                    <th>Estado</th>
                    <th aria-label="Acciones" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((s) => (
                    <tr key={s.id} style={{ opacity: s.isActive ? 1 : 0.6 }}>
                      <td>
                        <span className="bf-cell-main">{s.name}</span>
                        <div className="bf-cell-sub">{s.email}</div>
                      </td>
                      <td className="bf-cell-sub" style={{ minWidth: 150 }}>
                        {partner ? (
                          <>
                            {s.territory.length ? territoryLabel(s.territory) : 'Sin territorio'}
                            <div>
                              {s.exclusive && <Badge tone="info">Exclusivo</Badge>}{' '}
                              {s.visibility === 'PORTFOLIO' ? <Badge>Ve su cartera</Badge> : <Badge>Solo comisiones</Badge>}
                            </div>
                            {s.agreementTo && <div>Acuerdo hasta el {formatDate(s.agreementTo)}</div>}
                          </>
                        ) : (
                          [s.locality, s.province].filter(Boolean).join(', ') || '—'
                        )}
                      </td>
                      <td className="bf-cell-sub" style={{ minWidth: 130 }}>
                        <span className="bf-num bf-cell-main">{s.ratePct} %</span> licencia
                        {s.exclusive && s.territoryRatePct ? <div>Territorio {s.territoryRatePct} %</div> : null}
                        {s.renewalEnabled && <div>Mantenimiento {s.renewalRatePct} %</div>}
                        {s.bifiMonthlyRatePct ? <div>bifi mensual {s.bifiMonthlyRatePct} %</div> : null}
                        {s.subMonthlyRatePct ? <div>Abono mensual {s.subMonthlyRatePct} %</div> : null}
                      </td>
                      <td className="bf-cell-sub" style={{ minWidth: 160 }}>
                        {s.teamRatePct !== null && (
                          <>
                            <span className="bf-cell-main">Red al {s.teamRatePct} %</span>
                            <div>
                              {s.teamSize} {s.teamSize === 1 ? 'persona directa' : 'personas directas'} ·{' '}
                              {s.teamPayMode === 'LEADER' ? 'cobra por todos' : 'se paga a cada uno'}
                            </div>
                          </>
                        )}
                        {s.leaderName && <div>Debajo de {s.leaderName}</div>}
                        {s.teamRatePct === null && !s.leaderName && '—'}
                      </td>
                      <td className="bf-num" style={{ textAlign: 'right' }}>
                        {s.clients}
                      </td>
                      <td className="bf-num" style={{ textAlign: 'right' }}>
                        {formatUsd(s.pendingCents)}
                      </td>
                      <td className="bf-num" style={{ textAlign: 'right' }}>
                        {formatUsd(s.paidCents)}
                      </td>
                      <td>
                        {!s.isActive ? (
                          <Badge dot>De baja</Badge>
                        ) : s.hasLoggedIn ? (
                          <Badge tone="success" dot>
                            Activo
                          </Badge>
                        ) : (
                          <Badge tone="warning" dot>
                            No entró todavía
                          </Badge>
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <Menu>
                          <MenuTrigger asChild>
                            <Button size="sm" variant="ghost" aria-label={`Acciones de ${s.name}`} icon={<Ellipsis />} />
                          </MenuTrigger>
                          <MenuContent>
                            <MenuItem
                              icon={<Copy />}
                              onSelect={() =>
                                void copy(s.refUrl).then((ok) =>
                                  ok ? toast.success('Link copiado', s.refUrl) : toast.error('No se pudo copiar', s.refUrl),
                                )
                              }
                            >
                              Copiar su link
                            </MenuItem>
                            {manage && (
                              <>
                                <MenuItem icon={<Pencil />} onSelect={() => setEditing(s)}>
                                  {partner ? 'Editar ficha y acuerdo' : 'Editar ficha'}
                                </MenuItem>
                                <MenuItem icon={<KeyRound />} onSelect={() => reset.mutate(s)}>
                                  Nueva contraseña
                                </MenuItem>
                                <MenuItem icon={s.isActive ? <UserX /> : <UserCheck />} onSelect={() => active.mutate(s)}>
                                  {s.isActive ? 'Dar de baja' : 'Reactivar'}
                                </MenuItem>
                                <MenuItem icon={<Trash2 />} danger onSelect={() => setRemoving(s)}>
                                  Borrar
                                </MenuItem>
                              </>
                            )}
                          </MenuContent>
                        </Menu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {editing && (
        <PersonDialog
          kind={kind}
          initial={editing === 'new' ? undefined : editing}
          uplines={everyone.filter((s) => s.teamRatePct !== null && s.isActive && (editing === 'new' || s.id !== editing.id))}
          onClose={() => setEditing(null)}
          onDone={async (created) => {
            setEditing(null);
            if (created) {
              setSecret({
                title: `${created.seller.name} ya puede entrar`,
                email: created.seller.email,
                password: created.temporaryPassword,
                refUrl: created.seller.refUrl,
              });
            } else toast.success('Ficha guardada');
            await refresh();
          }}
        />
      )}
      {removing && (
        <Dialog open onOpenChange={(o) => !o && setRemoving(null)} title={`¿Borrar a ${removing.name}?`}>
          <div className="bf-form">
            <Callout tone="warning">
              Si nunca vendió, se borra del todo. Si ya tiene clientes, ventas o comisiones, queda dado de baja: no entra más ni suma ventas nuevas, y
              se guarda todo su historial. Las comisiones que le debés siguen apareciendo para pagarlas.
            </Callout>
            <div className="bf-form-actions">
              <Button onClick={() => setRemoving(null)}>Cancelar</Button>
              <Button variant="danger" loading={remove.isPending} onClick={() => remove.mutate(removing)}>
                Borrar
              </Button>
            </div>
          </div>
        </Dialog>
      )}
      {secret && (
        <Dialog open onOpenChange={(o) => !o && setSecret(null)} title={secret.title}>
          <div className="bf-form">
            <Callout>
              Le mandamos un email con el acceso. Por las dudas, acá está la contraseña temporal: se ve una sola vez. Al entrar activa la verificación
              en dos pasos y la cambia.
            </Callout>
            <Field label="Usuario">
              <Input readOnly value={secret.email} />
            </Field>
            <Field label="Contraseña temporal">
              <Input readOnly value={secret.password} style={{ fontFamily: 'monospace' }} onFocus={(e) => e.target.select()} />
            </Field>
            {secret.refUrl && (
              <Field label="Su link">
                <Input readOnly value={secret.refUrl} onFocus={(e) => e.target.select()} />
              </Field>
            )}
            <div className="bf-form-actions">
              <Button variant="primary" onClick={() => setSecret(null)}>
                Listo
              </Button>
            </div>
          </div>
        </Dialog>
      )}
    </>
  );
}

const num = (v: string) => (v.trim() === '' ? null : Number(v.replace(',', '.')));
const str = (v: number | null | undefined) => (v === null || v === undefined ? '' : String(v));

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <>
      <h3 className="bf-card-title" style={{ margin: '10px 0 0' }}>
        {title}
      </h3>
      {children}
    </>
  );
}

/** Territorio del acuerdo: países y, si se quiere, provincias o regiones de cada uno. */
function TerritoryEditor({ value, onChange }: { value: TerritoryArea[]; onChange: (v: TerritoryArea[]) => void }) {
  const [adding, setAdding] = useState('');
  const used = new Set(value.map((a) => a.country));
  const set = (i: number, a: TerritoryArea) => onChange(value.map((x, j) => (j === i ? a : x)));
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {value.map((a, i) => (
        <div key={a.country} style={{ border: '1px solid var(--bf-line)', borderRadius: 12, padding: '10px 12px', display: 'grid', gap: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <strong>{COUNTRY_NAMES[a.country] ?? a.country}</strong>
            <Button
              size="sm"
              variant="ghost"
              icon={<X />}
              aria-label={`Quitar ${COUNTRY_NAMES[a.country]}`}
              onClick={() => onChange(value.filter((_, j) => j !== i))}
            />
          </div>
          {a.country === 'AR' ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px' }}>
              {PROVINCES.map((p) => (
                <label key={p} className="bf-cell-sub" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    checked={a.regions.includes(p)}
                    onChange={(e) => set(i, { ...a, regions: e.target.checked ? [...a.regions, p] : a.regions.filter((r) => r !== p) })}
                  />
                  {p}
                </label>
              ))}
            </div>
          ) : (
            <Input
              placeholder="Regiones o provincias, separadas por coma (vacío: todo el país)"
              defaultValue={a.regions.join(', ')}
              onBlur={(e) =>
                set(i, {
                  ...a,
                  regions: e.target.value
                    .split(',')
                    .map((r) => r.trim())
                    .filter(Boolean),
                })
              }
            />
          )}
          <div className="bf-cell-sub">
            {a.regions.length ? `${a.regions.length} ${a.regions.length === 1 ? 'región' : 'regiones'}` : 'Todo el país'}
          </div>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8 }}>
        <Select value={adding} onChange={(e) => setAdding(e.target.value)} aria-label="Sumar país" style={{ maxWidth: 260 }}>
          <option value="">Sumar un país…</option>
          {COUNTRIES.filter(([c]) => !used.has(c)).map(([c, n]) => (
            <option key={c} value={c}>
              {n}
            </option>
          ))}
        </Select>
        <Button
          disabled={!adding}
          onClick={() => {
            onChange([...value, { country: adding, regions: [] }]);
            setAdding('');
          }}
        >
          Sumar
        </Button>
      </div>
    </div>
  );
}

function PersonDialog({
  kind,
  initial,
  uplines,
  onClose,
  onDone,
}: {
  kind: SellerKind;
  initial?: SellerDTO;
  uplines: SellerDTO[];
  onClose: () => void;
  onDone: (created: SellerCreated | null) => Promise<void>;
}) {
  const partner = kind === 'PARTNER';
  const [f, setF] = useState({
    name: initial?.name ?? '',
    email: initial?.email ?? '',
    phone: initial?.phone ?? '',
    taxId: initial?.taxId ?? '',
    country: initial?.country ?? 'AR',
    province: initial?.province ?? '',
    locality: initial?.locality ?? '',
    address: initial?.address ?? '',
    payoutCbu: initial?.payoutCbu ?? '',
    payoutAlias: initial?.payoutAlias ?? '',
    payoutWallet: initial?.payoutWallet ?? '',
    payoutNotes: initial?.payoutNotes ?? '',
    refCode: initial?.refCode ?? '',
    ratePct: String(initial?.ratePct ?? (partner ? 25 : 15)),
    upsellRatePct: str(initial?.upsellRatePct),
    bifiOnceRatePct: str(initial?.bifiOnceRatePct),
    bifiMonthlyRatePct: str(initial?.bifiMonthlyRatePct),
    subFirstRatePct: str(initial?.subFirstRatePct),
    subMonthlyRatePct: str(initial?.subMonthlyRatePct),
    leaderId: initial?.leaderId ?? '',
    hasNetwork: initial ? initial.teamRatePct !== null : partner,
    teamRatePct: initial?.teamRatePct != null ? String(initial.teamRatePct) : partner ? '25' : '20',
    teamPayMode: (initial?.teamPayMode ?? 'EACH') as TeamPayMode,
    renewalEnabled: initial?.renewalEnabled ?? false,
    renewalRatePct: str(initial?.renewalRatePct),
    activeMonths: String(initial?.activeMonths ?? (partner ? 0 : 6)),
    notes: initial?.notes ?? '',
    territory: initial?.territory ?? ([] as TerritoryArea[]),
    exclusive: initial?.exclusive ?? false,
    territoryRatePct: str(initial?.territoryRatePct),
    agreementFrom: initial?.agreementFrom ?? '',
    agreementTo: initial?.agreementTo ?? '',
    agreementNotes: initial?.agreementNotes ?? '',
    visibility: (initial?.visibility ?? 'COMMISSION') as Visibility,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const upline = uplines.find((l) => l.id === f.leaderId);

  const save = async () => {
    setBusy(true);
    setError(null);
    setErrors({});
    const body = {
      kind,
      name: f.name,
      email: f.email,
      phone: f.phone,
      taxId: f.taxId,
      country: f.country,
      province: f.country === 'AR' ? f.province : f.province,
      locality: f.locality,
      address: f.address,
      payoutCbu: f.payoutCbu,
      payoutAlias: f.payoutAlias,
      payoutWallet: f.payoutWallet,
      payoutNotes: f.payoutNotes,
      refCode: f.refCode,
      ratePct: num(f.ratePct) ?? 0,
      upsellRatePct: num(f.upsellRatePct),
      bifiOnceRatePct: num(f.bifiOnceRatePct),
      bifiMonthlyRatePct: num(f.bifiMonthlyRatePct),
      subFirstRatePct: num(f.subFirstRatePct),
      subMonthlyRatePct: num(f.subMonthlyRatePct),
      leaderId: f.leaderId || null,
      teamRatePct: f.hasNetwork ? num(f.teamRatePct) : null,
      teamPayMode: f.teamPayMode,
      renewalEnabled: f.renewalEnabled,
      renewalRatePct: f.renewalEnabled ? num(f.renewalRatePct) : null,
      activeMonths: Number(f.activeMonths) || 0,
      notes: f.notes,
      territory: f.territory,
      exclusive: f.exclusive,
      territoryRatePct: f.exclusive ? num(f.territoryRatePct) : null,
      agreementFrom: f.agreementFrom,
      agreementTo: f.agreementTo,
      agreementNotes: f.agreementNotes,
      visibility: f.visibility,
    };
    try {
      if (initial) {
        await api.put(`/platform/sellers/${initial.id}`, body);
        await onDone(null);
      } else await onDone(await api.post<SellerCreated>('/platform/sellers', body));
    } catch (err) {
      setErrors(fieldsOf(err));
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={initial ? `Ficha de ${initial.name}` : partner ? 'Sumar partner' : 'Sumar vendedor'}
      wide
    >
      <div className="bf-form">
        {error && <Callout tone="danger">{error}</Callout>}
        <div className="bf-form-row">
          <Field label={partner ? 'Nombre o empresa' : 'Nombre y apellido'} error={errors.name}>
            <Input value={f.name} onChange={(e) => set('name', e.target.value)} maxLength={100} autoFocus />
          </Field>
          <Field label="Documento (DNI, CUIT u otro)" error={errors.taxId}>
            <Input value={f.taxId} onChange={(e) => set('taxId', e.target.value)} maxLength={20} />
          </Field>
        </div>
        <div className="bf-form-row">
          <Field label="Email" hint="Con este entra al panel" error={errors.email}>
            <Input type="email" value={f.email} onChange={(e) => set('email', e.target.value)} maxLength={160} />
          </Field>
          <Field label="Teléfono" error={errors.phone}>
            <Input value={f.phone} onChange={(e) => set('phone', e.target.value)} maxLength={40} />
          </Field>
        </div>
        <div className="bf-form-row">
          <Field label="País">
            <Select value={f.country} onChange={(e) => set('country', e.target.value)}>
              {COUNTRIES.map(([c, n]) => (
                <option key={c} value={c}>
                  {n}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Provincia o región">
            {f.country === 'AR' ? (
              <Select value={f.province} onChange={(e) => set('province', e.target.value)}>
                <option value="">Elegí…</option>
                {PROVINCES.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </Select>
            ) : (
              <Input value={f.province} onChange={(e) => set('province', e.target.value)} maxLength={60} />
            )}
          </Field>
          <Field label="Localidad">
            <Input value={f.locality} onChange={(e) => set('locality', e.target.value)} maxLength={80} />
          </Field>
        </div>
        <Field label="Dirección">
          <Input value={f.address} onChange={(e) => set('address', e.target.value)} maxLength={160} />
        </Field>

        {partner && (
          <Section title="Acuerdo">
            <Field
              label="Territorio"
              hint="Países y, si querés, provincias o regiones. Sin territorio: vende donde sea, sin exclusividad."
              error={errors.territory}
            >
              <TerritoryEditor value={f.territory} onChange={(v) => set('territory', v)} />
            </Field>
            <label style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <Switch checked={f.exclusive} onCheckedChange={(v) => set('exclusive', v)} label="Exclusividad en su territorio" />
              Exclusividad en su territorio
            </label>
            {f.exclusive && (
              <Field
                label="% por las ventas de su territorio que no hizo su red"
                hint="Por ejemplo, un cliente de su zona que compra directo en la web. Vacío o 0: no cobra."
                error={errors.territoryRatePct}
              >
                <Input
                  inputMode="decimal"
                  value={f.territoryRatePct}
                  onChange={(e) => set('territoryRatePct', e.target.value)}
                  style={{ maxWidth: 160 }}
                />
              </Field>
            )}
            <div className="bf-form-row">
              <Field label="Vigente desde" error={errors.agreementFrom}>
                <Input type="date" value={f.agreementFrom} onChange={(e) => set('agreementFrom', e.target.value)} />
              </Field>
              <Field label="Hasta" hint="Vacío: sin vencimiento" error={errors.agreementTo}>
                <Input type="date" value={f.agreementTo} onChange={(e) => set('agreementTo', e.target.value)} />
              </Field>
            </div>
            <Field label="Qué ve de sus clientes">
              <Select value={f.visibility} onChange={(e) => set('visibility', e.target.value as Visibility)}>
                {VISIBILITIES.map((v) => (
                  <option key={v} value={v}>
                    {VISIBILITY_LABELS[v]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Notas del acuerdo" hint="Lo que se pactó y no entra en los campos (solo lo ve el admin)">
              <Textarea value={f.agreementNotes} onChange={(e) => set('agreementNotes', e.target.value)} rows={3} maxLength={4000} />
            </Field>
          </Section>
        )}

        <Section title="Comisión (en dólares, sobre lo cobrado)">
          <div className="bf-form-row">
            <Field label="Licencia (primer pago)" error={errors.ratePct}>
              <Input inputMode="decimal" value={f.ratePct} onChange={(e) => set('ratePct', e.target.value)} />
            </Field>
            <Field label="Ampliaciones" hint="Locales, franquicias y adicionales. Vacío: igual que la licencia" error={errors.upsellRatePct}>
              <Input inputMode="decimal" value={f.upsellRatePct} onChange={(e) => set('upsellRatePct', e.target.value)} />
            </Field>
          </div>
          <div className="bf-form-row">
            <Field label="bifi, pago único" hint="Vacío: igual que la licencia" error={errors.bifiOnceRatePct}>
              <Input inputMode="decimal" value={f.bifiOnceRatePct} onChange={(e) => set('bifiOnceRatePct', e.target.value)} />
            </Field>
            <Field label="bifi, abono mensual" hint="Vacío o 0: no cobra" error={errors.bifiMonthlyRatePct}>
              <Input inputMode="decimal" value={f.bifiMonthlyRatePct} onChange={(e) => set('bifiMonthlyRatePct', e.target.value)} />
            </Field>
          </div>
          <div className="bf-form-row">
            <Field label="Abono del sistema, primer mes" hint="Vacío: igual que la licencia" error={errors.subFirstRatePct}>
              <Input inputMode="decimal" value={f.subFirstRatePct} onChange={(e) => set('subFirstRatePct', e.target.value)} />
            </Field>
            <Field label="Abono del sistema, meses siguientes" hint="Vacío o 0: no cobra (solo el primer mes)" error={errors.subMonthlyRatePct}>
              <Input inputMode="decimal" value={f.subMonthlyRatePct} onChange={(e) => set('subMonthlyRatePct', e.target.value)} />
            </Field>
          </div>
          <label style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <Switch checked={f.renewalEnabled} onCheckedChange={(v) => set('renewalEnabled', v)} label="Cobra el mantenimiento anual" />
            Cobra comisión por el mantenimiento anual de sus clientes
          </label>
          <div className="bf-form-row">
            {f.renewalEnabled && (
              <Field label="Mantenimiento anual" error={errors.renewalRatePct}>
                <Input inputMode="decimal" value={f.renewalRatePct} onChange={(e) => set('renewalRatePct', e.target.value)} />
              </Field>
            )}
            <Field
              label="Meses sin vender"
              hint="Si no vendió en este plazo, no cobra mantenimiento, abonos ni ampliaciones de sus clientes. 0: sin condición"
              error={errors.activeMonths}
            >
              <Input type="number" min={0} max={36} value={f.activeMonths} onChange={(e) => set('activeMonths', e.target.value)} />
            </Field>
            <Field label="Código del link" hint="bifeai.com/?ref=… (vacío: se arma con el nombre)" error={errors.refCode}>
              <Input value={f.refCode} onChange={(e) => set('refCode', e.target.value.toLowerCase())} maxLength={30} />
            </Field>
          </div>
        </Section>

        <Section title="Red">
          <Field
            label="Arriba suyo"
            hint={
              upline
                ? `${upline.name} tiene su red al ${upline.teamRatePct} %: se queda con la diferencia.`
                : 'Opcional. Jefe o partner del que depende.'
            }
            error={errors.leaderId}
          >
            <Select value={f.leaderId} onChange={(e) => set('leaderId', e.target.value)}>
              <option value="">Nadie</option>
              {uplines.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name} ({l.kind === 'PARTNER' ? 'partner' : 'vendedor'}, red al {l.teamRatePct} %)
                </option>
              ))}
            </Select>
          </Field>
          <label style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <Switch checked={f.hasNetwork} onCheckedChange={(v) => set('hasNetwork', v)} label="Tiene gente a cargo" />
            Tiene vendedores a cargo (su red)
          </label>
          {f.hasNetwork && (
            <div className="bf-form-row">
              <Field
                label="% de su red"
                hint="Por cada venta de su red se queda con la diferencia con el % de quien vendió"
                error={errors.teamRatePct}
              >
                <Input inputMode="decimal" value={f.teamRatePct} onChange={(e) => set('teamRatePct', e.target.value)} />
              </Field>
              <Field label="Cómo se paga a su red">
                <Select value={f.teamPayMode} onChange={(e) => set('teamPayMode', e.target.value as TeamPayMode)}>
                  {TEAM_PAY_MODES.map((m) => (
                    <option key={m} value={m}>
                      {TEAM_PAY_MODE_LABELS[m]}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          )}
        </Section>

        <Section title="Datos para pagarle">
          <div className="bf-form-row">
            <Field label="CBU / CVU / cuenta">
              <Input value={f.payoutCbu} onChange={(e) => set('payoutCbu', e.target.value)} maxLength={40} />
            </Field>
            <Field label="Alias">
              <Input value={f.payoutAlias} onChange={(e) => set('payoutAlias', e.target.value)} maxLength={60} />
            </Field>
          </div>
          <Field label="Billetera cripto (red y dirección)">
            <Input value={f.payoutWallet} onChange={(e) => set('payoutWallet', e.target.value)} maxLength={200} />
          </Field>
          <Field label="Otros datos de pago">
            <Input value={f.payoutNotes} onChange={(e) => set('payoutNotes', e.target.value)} maxLength={500} />
          </Field>
          <Field label="Notas internas">
            <Textarea value={f.notes} onChange={(e) => set('notes', e.target.value)} rows={2} maxLength={2000} />
          </Field>
        </Section>
        <div className="bf-form-actions">
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" loading={busy} onClick={() => void save()}>
            {initial ? 'Guardar' : 'Sumar y mandarle el acceso'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
